import { z } from 'zod';
import { arbitrationCommands } from './analysis-arbitration.ts';
import { pvSheetTender } from '../../verticals/pv/sheet/schema.ts';

export const analysisHash = z.string().regex(/^[a-f0-9]{64}$/);
function containsUnknown(input: unknown, output: unknown): boolean {
    if (input === null || typeof input !== 'object') return false;
    if (output === null || typeof output !== 'object') return true;
    const parsed = output as Record<string, unknown>;
    return Object.entries(input).some(
        ([key, value]) => !Object.hasOwn(parsed, key) || containsUnknown(value, parsed[key]),
    );
}
const businessOutput = z
    .json()
    .refine((value) => BufferSafeSize(value) <= 1024 * 1024, 'analysis_json_limit')
    .superRefine((input, ctx) => {
        const result = pvSheetTender.safeParse(input);
        if (!result.success || containsUnknown(input, result.data))
            ctx.addIssue({ code: 'custom', message: 'analysis_business_output_invalid' });
    })
    .transform((input) => pvSheetTender.parse(input));
const key = z.string().regex(/^[a-z0-9][a-z0-9-]{0,99}$/);
const code = z.string().regex(/^[a-z][a-z0-9_.-]{0,99}$/);
const json = z.json().refine((value) => BufferSafeSize(value) <= 1024 * 1024, 'analysis_json_limit');
function BufferSafeSize(value: unknown) {
    return new TextEncoder().encode(JSON.stringify(value)).length;
}
export const analysisReview = z
    .looseObject({
        state: z.enum(['blocked', 'review_required', 'reviewed']),
        coverage: z.array(json).max(1000),
        unresolvedIssues: z.array(json).max(1000),
        evidence: json,
    })
    .refine((value) => value.state !== 'reviewed' || value.unresolvedIssues.length === 0, 'analysis_unresolved_review')
    .refine((value) => BufferSafeSize(value) <= 1024 * 1024, 'analysis_review_limit');
export const analysisProvenance = z
    .strictObject({
        skill: z.strictObject({ version: z.string().min(1).max(200), sha256: analysisHash.nullable() }),
        contract: z.strictObject({ version: z.string().min(1).max(200), sha256: analysisHash.nullable() }),
        repository: z.strictObject({
            commit: z
                .string()
                .regex(/^[a-f0-9]{40}$/)
                .nullable(),
            dirty: z.boolean(),
            files: z.record(z.string().max(500), analysisHash),
        }),
        tools: json,
        model: z.strictObject({
            provider: code.nullable(),
            id: z.string().min(1).max(200).nullable(),
            revision: z.string().min(1).max(200).nullable(),
            settings: json,
        }),
        unavailable: z.array(z.string().min(1).max(500)).max(100),
    })
    .refine((value) => BufferSafeSize(value) <= 1024 * 1024, 'analysis_provenance_limit');
export const analysisRuleScope = z.strictObject({
    vertical: key.nullable(),
    task: key.nullable(),
    tags: z.array(key).max(50),
});
// A scope conclusion is independent of the completeness/review of extracted PV facts.
export const analysisScopeDecision = z
    .strictObject({
        outcome: z.literal('out_of_scope'),
        reason: code,
        rationale: z.string().min(1).max(20000),
        evidence: z
            .array(
                z.strictObject({
                    document: z.string().min(1).max(1000),
                    page: z.number().int().positive().nullable(),
                    excerpt: z.string().min(1).max(10000),
                }),
            )
            .min(1)
            .max(100),
    })
    .refine((value) => BufferSafeSize(value) <= 1024 * 1024, 'analysis_json_limit');
const runFields = {
    operation: z.literal('save-run'),
    id: z.uuid(),
    review: analysisReview,
    raw: json.nullable(),
    finishedAt: z.iso.datetime({ offset: true }),
    metrics: json,
    supporting: z
        .strictObject({
            initialReview: json.optional(),
            evidence: json.optional(),
            manifest: json.optional(),
            decisions: json.optional(),
        })
        .optional(),
};
const saveRun = z.discriminatedUnion('outcome', [
    z.strictObject({
        ...runFields,
        outcome: z.enum(['succeeded', 'blocked', 'failed']),
        output: json.nullable(),
        outputKind: z.enum(['complete', 'draft', 'none']),
        pdfBase64: z
            .string()
            .max(12 * 1024 * 1024)
            .nullable(),
        errorCode: code.nullable(),
    }),
    z.strictObject({
        ...runFields,
        outcome: z.literal('out_of_scope'),
        scopeDecision: analysisScopeDecision,
        output: z.null(),
        outputKind: z.literal('none'),
        pdfBase64: z.null(),
        errorCode: z.null(),
    }),
]);
export const analysisCommands = z.discriminatedUnion('operation', [
    ...arbitrationCommands.options,
    z.strictObject({
        operation: z.literal('register-case'),
        id: z.uuid(),
        key,
        archiveId: z.uuid(),
        title: z.string().min(1).max(300),
        vertical: z.literal('pv'),
        task: z.literal('dce-to-json'),
        purpose: z.enum(['development', 'validation']),
        platform: code.nullable(),
        externalId: z.string().max(200).nullable(),
        buyerReference: z.string().max(300).nullable(),
        noticeUrl: z.url().nullable(),
        downloadUrl: z.url().nullable(),
        publishedOn: z.iso.date().nullable(),
        acquiredAt: z.iso.datetime({ offset: true }),
        tags: z.array(key).max(50),
        previousCaseId: z.uuid().nullable(),
    }),
    z.strictObject({
        operation: z.literal('prepare-run'),
        id: z.uuid(),
        caseId: z.uuid(),
        mode: z.enum(['new', 'historical']),
        historicalContext: json.nullable(),
        provenance: analysisProvenance,
        startedAt: z.iso.datetime({ offset: true }),
        previousRunId: z.uuid().nullable(),
    }),
    saveRun,
    z.strictObject({
        operation: z.literal('save-reference'),
        id: z.uuid(),
        caseId: z.uuid(),
        runId: z.uuid(),
        number: z.number().int().min(1).max(100000),
        expected: businessOutput,
        review: analysisReview,
        decisionIds: z.array(z.uuid()).max(1000),
        confirmReviewed: z.boolean(),
    }),
    z.strictObject({
        operation: z.literal('save-scope-reference'),
        id: z.uuid(),
        caseId: z.uuid(),
        runId: z.uuid(),
        number: z.number().int().min(1).max(100000),
        classification: analysisScopeDecision,
        decisionIds: z.array(z.uuid()).min(1).max(1000),
        approval: z.discriminatedUnion('state', [
            z.strictObject({
                state: z.literal('review_required'),
                scope: z.literal('scope_rejection_only'),
                confirmHumanApproval: z.literal(false),
            }),
            z.strictObject({
                state: z.literal('approved'),
                scope: z.literal('scope_rejection_only'),
                confirmHumanApproval: z.literal(true),
            }),
        ]),
    }),
    z.strictObject({
        operation: z.literal('record-decision'),
        id: z.uuid(),
        runId: z.uuid(),
        supersedesId: z.uuid().nullable(),
        fields: z.array(z.string().min(1).max(300)).min(1).max(100),
        facts: json,
        resolution: z.string().min(1).max(20000),
        justification: z.string().min(1).max(20000),
    }),
    z.strictObject({
        operation: z.literal('propose-rule'),
        id: z.uuid(),
        ruleKey: key,
        version: z.number().int().min(1).max(100000),
        decisionId: z.uuid(),
        instruction: z.string().min(1).max(20000),
        conditions: z.string().min(1).max(20000).optional(),
        scope: analysisRuleScope,
        conflictKey: key.nullable(),
    }),
    z.strictObject({
        operation: z.literal('approve-rule'),
        id: z.uuid(),
        revisionId: z.uuid(),
        confirmHumanApproval: z.literal(true),
    }),
    z.strictObject({
        operation: z.literal('retire-rule'),
        id: z.uuid(),
        revisionId: z.uuid(),
        reason: z.string().min(1).max(2000),
    }),
    z.strictObject({
        operation: z.literal('get-case'),
        id: z.uuid(),
        page: z.number().int().min(1).max(100000).default(1),
    }),
    z.strictObject({ operation: z.literal('get-run'), id: z.uuid() }),
    z.strictObject({ operation: z.literal('list-cases'), page: z.number().int().min(1).max(100000).default(1) }),
    z.strictObject({ operation: z.literal('list-rules'), page: z.number().int().min(1).max(100000).default(1) }),
    z.strictObject({
        operation: z.literal('download'),
        caseId: z.uuid(),
        runId: z.uuid().nullable(),
        referenceNumber: z.number().int().positive().nullable(),
        artifact: z.enum([
            'dce.zip',
            'output.json',
            'review.json',
            'raw.json',
            'sheet.pdf',
            'expected.json',
            'decisions.json',
            'metadata.json',
            'classification.json',
            'review.initial.json',
            'evidence.json',
            'manifest.json',
            'decisions.original.json',
        ]),
    }),
    z.strictObject({
        operation: z.literal('export-case'),
        id: z.uuid(),
        caseId: z.uuid(),
        referenceNumber: z.number().int().positive(),
        runIds: z.array(z.uuid()).max(100),
    }),
]);
export type AnalysisCommand = z.infer<typeof analysisCommands>;
export const analysisCommandResult = z.strictObject({ result: z.json() });
export const analysisConflictReason = z.enum([
    'analysis_archive_not_ready',
    'analysis_stale_state',
    'analysis_questions_blocking',
    'analysis_arbitration_resume_required',
    'analysis_code_change_required',
    'analysis_transmission_invalid',
    'analysis_artifact_missing',
    'analysis_business_output_invalid',
    'analysis_case_key_conflict',
    'analysis_command_invalid',
    'analysis_decision_case_mismatch',
    'analysis_export_storage_not_separate',
    'analysis_historical_context_invalid',
    'analysis_idempotency_conflict',
    'analysis_pdf_invalid',
    'analysis_record_invalid',
    'analysis_reference_not_reviewed',
    'analysis_reference_number_conflict',
    'analysis_result_invalid',
    'analysis_review_confirmation_required',
    'analysis_rule_conflict',
    'analysis_rule_not_active',
    'analysis_rule_version_conflict',
    'analysis_run_case_mismatch',
    'analysis_split_conflict',
    'analysis_storage_unavailable',
    'analysis_time_invalid',
    'analysis_validation_learning_forbidden',
]);
export const analysisRuleConflict = z.strictObject({
    conflictKey: key,
    revisionIds: z.array(z.uuid()).min(2),
});
export const analysisCommandConflict = z.strictObject({
    error: z.strictObject({
        code: z.literal('conflict'),
        reason: analysisConflictReason,
        conflicts: z.array(analysisRuleConflict).optional(),
    }),
});
